import { z } from 'zod';

const OptionContract = z.object({
  isin: z.string().nullable(),
  marketOpenPositions: z.number(),
  initialMargin: z.number(),
  requiredMargin: z.number(),
  maintenanceMargin: z.number(),
  strikePrice: z.number(),
  contractSize: z.number(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  cashSettlementDate: z.string().nullable(),
  physicalSettlementDate: z.string().nullable(),
  individualTraderOpenPositionUpperLimit: z.number(),
  firmTraderOpenPositionUpperLimit: z.number(),
  brokerOpenPositionUpperLimit: z.number(),
  marketOpenPositionUpperLimit: z.number(),
  numberOfOrdersUpperLimit: z.number(),
  underlyingAssetIsin: z.string().nullable(),
  mixedSettlement: z.boolean(),
  previousDayClosingPrice: z.number(),
  blackScholes: z.object({
    underlyingAssetPreviousDayClosingPrice: z.number(),
    fairPrice: z.number(),
    delta: z.number(),
    theta: z.number(),
    rho: z.number(),
    gamma: z.number(),
    vega: z.number(),
  }),
  underlyingAssetLastTradePrice: z.number(),
  underlyingAssetReferencePriceVariation: z.number(),
  underlyingAssetReferencePriceVariationPercentage: z.number(),
  underlyingAsseClosingPrice: z.number(),
  underlyingAssetClosingPriceVariation: z.number(),
  underlyingAssetClosingPriceVariationPercentage: z.number(),
  contractType: z.number(),
  cashSettlementFee: z.number(),
  physicalsettlementfee: z.number(),
  underlyingAssetName: z.string(),
  delta: z.number().nullable(),
  sameRiskPositions: z.number().nullable(),
});

const OptionContractRequest = z.object({
  isin: z.string(),
});

const SpreadQuantityItem = z.object({
  instrumentName: z.string().nullable(),
  quantity: z.number().nullable(),
});

const StrategyQuantitiyItem = z.object({
  nakedQuantity: z.number().nullable(),
  coveredQuantity: z.number().nullable(),
  spreadQuantities: z.array(SpreadQuantityItem).nullable(),
});

const PositionItem = z.object({
  allowCashSettlementRequest: z.boolean(),
  allowPhysicalSettlementRequest: z.boolean(),
  isin: z.string(),
  instrumentName: z.string(),
  failedToDeliverQuantity: z.number(),
  quantity: z.number(),
  netAveragePrice: z.number(),
  cashSettlementDate: z.string(),
  physicalSettlementDate: z.string(),
  physicalSettlementRequestQuantity: z.number(),
  cashSettlementRequestQuantity: z.number(),
  cashSettlementQuantity: z.number(),
  contractType: z.number(),
  contractSize: z.number(),
  physicalSettlementQuantity: z.number(),
  strikePrice: z.number(),
  positionSide: z.number(),
  lastTradePrice: z.number(),
  optionAccountStatus: z.number(),
  increaseMargin: z.number(),
  instrumentTypeCode: z.number(),
  cashSettlementFee: z.number(),
  shareInProportion: z.boolean(),
  cashMaximumQuantity: z.boolean(),
  physicalMaximumQuantity: z.boolean(),
  cashRequestState: z.enum(['1', '2', '3', '4']),
  physicalRequestState: z.enum(['1', '2', '3', '4']),
  requestedInCaseOfOtmOrAtm: z.boolean().nullable(),
  underlyingAssetName: z.string(),
  underlyingAsseClosingPrice: z.number(),
  underlyingAssetClosingPriceVariation: z.number(),
  underlyingAssetClosingPriceVariationPercentage: z.number(),
  underlyingAssetIsin: z.string(),
  underlyingAssetLastTradePrice: z.number(),
  underlyingAssetReferencePriceVariation: z.number(),
  underlyingAssetReferencePriceVariationPercentage: z.number(),
  underlyingAssetTitle: z.string().nullable(),
  sellTaxRate: z.number(),
  physicalSettlementFee: z.number(),
  referencePriceVariationPercentage: z.number(),
  // Mock
  blockedMargin: z.number().nullable(),
  tradingState: z.number().nullable(),
  sectorCode: z.string().nullable(),
  subSectorCode: z.string().nullable(),
  physicalSettlementRequestAccess: z.boolean().nullable(),
  cashSettlementRequestAccess: z.boolean().nullable(),
  physicalSettlementTaxFactor: z.number().nullable(),

  collateralQuantity: z.number().nullable(),
  strategyQuantities: StrategyQuantitiyItem.nullable(),
});

const Positions = z.object({
  items: z.array(PositionItem),
  totalCount: z.number(),
});

const OptionPositionsRequestParams = z.object({
  pageSize: z.number(),
  pageNumber: z.number(),
});

const StrategyItem = z.object({
  strategyType: z.number().nullable(),
  quantity: z.number().nullable(),
  firstLegInstrumentName: z.string().nullable(),
  firstLegStrikePrice: z.number().nullable(),
  secondLegInstrumentName: z.string().nullable(),
  secondLegStrikePrice: z.number().nullable(),
  physicalSettlementDate: z.string().nullable(),
});

const OptionStrategyLegsParams = z.object({
  contractInfoId: z.string(),
  pageSize: z.number(),
  pageNumber: z.number(),
});

const StrategyLegsItem = z.object({
  instrumentName: z.string(),
  isin: z.string(),
  quantity: z.number(),
  frozenQuantity: z.number(),
  availableQuantity: z.number(),
  strikePrice: z.number(),
});

const Strategies = z.object({
  items: z.array(StrategyItem),
  totalCount: z.number(),
});

const StrategyLegs = z.object({
  items: z.array(StrategyLegsItem),
  totalCount: z.number(),
});

const OptionStrategiesRequestParams = z.object({
  pageSize: z.number(),
  pageNumber: z.number(),
});

const UpdateStrategyRequestParams = z.object({
  isin: z.string(),
  quantity: z.number(),
});

const CashSettlementRequest = z.object({
  isin: z.string(),
  quantity: z.number(),
  maximumQuantity: z.boolean(),
  shareInProportion: z.boolean().nullable(),
});

const PhysicalSettlementRequest = z.object({
  isin: z.string(),
  quantity: z.number(),
  maximumQuantity: z.boolean(),
  requestedInCaseOfOtmOrAtm: z.boolean().nullable(),
});

const BaseQuery = z.object({
  totalCount: z.number(),
  items: z.array(z.object({})).nullable(),
});

const UnderlyingAssetIsin = z.object({
  isin: z.string(),
  instrumentName: z.string(),
  lastTradePrice: z.number(),
  referencePriceVariation: z.number(),
  referencePriceVariationPercentage: z.number(),
  closingPrice: z.number(),
  closingPriceVariation: z.number(),
  closingPriceVariationPercentage: z.number(),
  instrumentTradingState: z.number(),
  totalTradeValue: z.number(),
  totalNumberOfSharesTraded: z.number(),
});

const UnderlyingAssetIsinsQuery = BaseQuery.omit({
  items: true,
}).extend({
  underlyingInstrumentIsinItems: UnderlyingAssetIsin.array(),
});

const UnderlyingEndDatesRequest = z.object({
  underlyingInstrumentIsin: z.string(),
});

const UnderlyingEndDateQuery = z.object({
  endDates: z.array(z.string()),
});

const OptionContractsChainRequest = z.object({
  underlyingAssetIsin: z.string(),
  endDate: z.string(),
  contractType: z.number(),
});

const OptionContractChainDetails = z.object({
  isin: z.string().nullable(),
  instrumentName: z.string(),
  contractType: z.number(),
  strikePrice: z.number(),
  marketOpenPositions: z.number(),
  contractSize: z.number(),
  lastTradePrice: z.number(),
  referencePriceVariation: z.number(),
  referencePriceVariationPercentage: z.number(),
  closingPrice: z.number(),
  closingPriceVariation: z.number(),
  closingPriceVariationPercentage: z.number(),
  totalNumberOfSharesTraded: z.number(),
  totalTradeValue: z.number(),
  instrumentTradingState: z.number(),
});

const OptionContractChain = z.object({
  underlyingInstrumentIsin: z.string(),
  underlyingAssetLastTradePrice: z.number(),
  endDate: z.string(),
  openPositionUpperLimit: z.number(),
  openPositionLowerLimit: z.number(),
  numberOfOpenPosition: z.number().nullable(),
  allowOpeningOrder: z.number().nullable(),
  blackScholesVolatility: z.number(),
  blackScholesRateOfReturn: z.number(),
  cashSettlementFee: z.number(),
  failedToDeliverTax: z.number(),
  remainingDays: z.number(),
  contractChainDetails: OptionContractChainDetails.array().nullable(),
});

const PositionHistoryRequest = z.object({
  Isin: z.string().array().optional(),
  StarttDate: z.string().optional(),
  EndDate: z.string().optional(),
  TraderDirection: z.number().optional(),
  IsSettled: z.boolean().optional(),
  PageNumber: z.number(),
  PageSize: z.number(),
});

const PositionHistoryItem = z.object({
  isin: z.string(),
  instrumentName: z.string(),
  quantity: z.number(),
  netAveragePrice: z.number(),
  cashSettlementQuantity: z.number().nullable(),
  cashSettlementDate: z.string().nullable(),
  physicalSettlementDate: z.string().nullable(),
  physicalSettlementQuantity: z.number().nullable(),
  strikePrice: z.number().nullable(),
  traderDirection: z.number(),
  margin: z.number().nullable(),
  insertDate: z.string(),
  endDate: z.string(),
  contractSize: z.number(),
  instrumentType: z.number(),
  settlementType: z.number(),
  failedToDeliverQuantity: z.number(),
  physicalSettlementFee: z.number(),
  underlyingAssetIsin: z.string(),
  underlyingAssetLastTradePrice: z.number(),
  underlyingAssetReferencePriceVariation: z.number(),
  underlyingAssetReferencePriceVariationPercentage: z.number(),
  underlyingAsseClosingPrice: z.number(),
  underlyingAssetClosingPriceVariation: z.number(),
  underlyingAssetClosingPriceVariationPercentage: z.number(),
  strategyQuantities: StrategyQuantitiyItem.nullable(),
});

const PositionHistory = z.object({
  totalCount: z.number(),
  items: PositionHistoryItem.array().nullable(),
});

const CashSettlementHistoryRequest = PositionHistoryRequest.extend({
  SettlementRequestStates: z.number().array().optional(),
});

const CashSettlementHistoryItem = z.object({
  traderFullName: z.string(),
  isin: z.string(),
  instrumentName: z.string(),
  positionQuantity: z.number(),
  settlementDate: z.string(),
  requestedQuantity: z.number().nullable(),
  settledQuantity: z.number().nullable(),
  requestedInAtmOrOtm: z.boolean().nullable(),
  maximumQuantity: z.boolean().nullable(),
  settlementRequestState: z.number(),
  traderDirection: z.number(),
  underlyingAssetIsin: z.string(),
  contractType: z.enum(['1', '2']),
  cashSettlementFee: z.number(),
  physicalSettlementFee: z.number(),
});

const CashSettlementHistory = z.object({
  totalCount: z.number(),
  items: CashSettlementHistoryItem.array().nullable(),
});

const PhysicalSettlementHistoryRequest = PositionHistoryRequest.extend({
  SettlementRequestStates: z.number().array().optional(),
});

const PhysicalSettlementHistoryItem = z.object({
  traderFullName: z.string(),
  isin: z.string(),
  instrumentName: z.string(),
  positionQuantity: z.number(),
  settlementDate: z.string(),
  requestedQuantity: z.number().nullable(),
  settledQuantity: z.number().nullable(),
  shareInProportion: z.boolean().nullable(),
  maximumQuantity: z.boolean().nullable(),
  settlementRequestState: z.number(),
  traderDirection: z.number(),
  underlyingAssetIsin: z.string(),
  contractType: z.enum(['1', '2']),
  cashSettlementFee: z.number(),
  physicalSettlementFee: z.number(),
});

const PhysicalSettlementHistory = z.object({
  totalCount: z.number(),
  items: PhysicalSettlementHistoryItem.array().nullable(),
});

const FrozenHistoryItem = z.object({
  isin: z.string(),
  name: z.string(),
  requestDate: z.string(),
  requestStringDate: z.string(),
  requestStatesId: z.number(),
  requestState: z.string(),
  requestId: z.string(),
  isFreeze: z.boolean(),
  description: z.string().nullable(),
});

const FrozenHistory = z.object({
  totalCount: z.number(),
  items: FrozenHistoryItem.array().nullable(),
});

const OptionFrozenHistoryParams = z.object({
  isFreeze: z.boolean(),
});

const FreezeUnfreezeQueryParams = z.object({
  instrumentIsin: z.string(),
  requestGatewayId: z.number(),
  executorBrokerBranchId: z.number(),
  description: z.string().nullable(),
});

type OptionContract = z.infer<typeof OptionContract>;
type OptionContractRequest = z.infer<typeof OptionContractRequest>;
type Positions = z.infer<typeof Positions>;
type PositionItem = z.infer<typeof PositionItem>;
type PositionHistory = z.infer<typeof PositionHistory>;
type PositionHistoryItem = z.infer<typeof PositionHistoryItem>;
type SpreadQuantityItem = z.infer<typeof SpreadQuantityItem>;
type StrategyQuantitiyItem = z.infer<typeof StrategyQuantitiyItem>;
type Strategies = z.infer<typeof Strategies>;
type StrategyLegs = z.infer<typeof StrategyLegs>;
type StrategyItem = z.infer<typeof StrategyItem>;
type CashSettlementHistory = z.infer<typeof CashSettlementHistory>;
type CashSettlementHistoryItem = z.infer<typeof CashSettlementHistoryItem>;
type PhysicalSettlementHistory = z.infer<typeof PhysicalSettlementHistory>;
type FrozenHistory = z.infer<typeof FrozenHistory>;
type FrozenHistoryItem = z.infer<typeof FrozenHistoryItem>;
type PhysicalSettlementHistoryItem = z.infer<
  typeof PhysicalSettlementHistoryItem
>;
type OptionPositionsRequestParams = z.infer<
  typeof OptionPositionsRequestParams
>;
type OptionStrategiesRequestParams = z.infer<
  typeof OptionStrategiesRequestParams
>;
type CashSettlementRequest = z.infer<typeof CashSettlementRequest>;
type UpdateStrategyRequestParams = z.infer<typeof UpdateStrategyRequestParams>;
type PhysicalSettlementRequest = z.infer<typeof PhysicalSettlementRequest>;
type BaseQuery = z.infer<typeof BaseQuery>;
type UnderlyingEndDatesRequest = z.infer<typeof UnderlyingEndDatesRequest>;
type UnderlyingEndDateQuery = z.infer<typeof UnderlyingEndDateQuery>;
type UnderlyingAssetIsin = z.infer<typeof UnderlyingAssetIsin>;
type UnderlyingAssetIsinsQuery = z.infer<typeof UnderlyingAssetIsinsQuery>;
type OptionContractsChainRequest = z.infer<typeof OptionContractsChainRequest>;
type OptionContractChainDetails = z.infer<typeof OptionContractChainDetails>;
type OptionContractChain = z.infer<typeof OptionContractChain>;
type PositionHistoryRequest = z.infer<typeof PositionHistoryRequest>;
type CashSettlementHistoryRequest = z.infer<
  typeof CashSettlementHistoryRequest
>;
type PhysicalSettlementHistoryRequest = z.infer<
  typeof PhysicalSettlementHistoryRequest
>;

type OptionFrozenHistoryParams = z.infer<typeof OptionFrozenHistoryParams>;
type FreezeUnfreezeQueryParams = z.infer<typeof FreezeUnfreezeQueryParams>;
type OptionStrategyLegsParams = z.infer<typeof OptionStrategyLegsParams>;

export {
  OptionContract,
  OptionContractRequest,
  PositionHistory,
  PositionHistoryItem,
  SpreadQuantityItem,
  StrategyQuantitiyItem,
  CashSettlementHistory,
  CashSettlementHistoryItem,
  PhysicalSettlementHistory,
  PhysicalSettlementHistoryItem,
  PositionItem,
  Positions,
  Strategies,
  StrategyLegs,
  StrategyItem,
  OptionPositionsRequestParams,
  OptionStrategiesRequestParams,
  UpdateStrategyRequestParams,
  CashSettlementRequest,
  PhysicalSettlementRequest,
  UnderlyingAssetIsin,
  UnderlyingAssetIsinsQuery,
  UnderlyingEndDatesRequest,
  UnderlyingEndDateQuery,
  OptionContractsChainRequest,
  OptionContractChainDetails,
  OptionContractChain,
  PositionHistoryRequest,
  CashSettlementHistoryRequest,
  PhysicalSettlementHistoryRequest,
  FrozenHistory,
  FrozenHistoryItem,
  OptionFrozenHistoryParams,
  FreezeUnfreezeQueryParams,
  OptionStrategyLegsParams,
};
